import { isAfter, parseISO } from 'date-fns';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, ConfirmDeleteModal, SelectFoodItemModal, Theme } from '../../../components';
import { ExitReason, FoodLine, FoodTray, FoodType, SelectedFoodTrayIndex } from '../../../models';
import { foodTrayTemplatesActions, foodTrayTemplatesSelectors } from '../../../redux';
import { getFoodTypeName } from '../../../redux/foodtypes/selectors';
import ActionMenu from './ActionMenu';
import FoodStatusModal from './FoodStatusModal';

import './Footer.scss';

interface Props {
  selectedFoodTray: SelectedFoodTrayIndex;
  containingFoodLineId: string;
  foodLines: FoodLine[];
}

const DashboardFooter: FC<Props> = ({ selectedFoodTray, containingFoodLineId, foodLines }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isReplaceFoodItemLoading = useSelector(foodTrayTemplatesSelectors.isReplaceFoodItemLoading);
  const isReplaceFoodItemAndStartTimerLoading = useSelector(foodTrayTemplatesSelectors.isReplaceFoodItemAndStartTimerLoading);
  const isEmptyItemLoading = useSelector(foodTrayTemplatesSelectors.isEmptyItemLoading);
  const isEmptyLineLoading = useSelector(foodTrayTemplatesSelectors.isEmptyLineLoading);
  const isStartTimerLoading = useSelector(foodTrayTemplatesSelectors.isStartTimerLoading);
  const isStartTimerForExistingFoodItemLoading = useSelector(foodTrayTemplatesSelectors.isStartTimerForExistingFoodItemLoading);

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isFoodStatusOpen, setIsFoodStatusOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [exitReason, setExitReason] = useState<ExitReason | null>(null);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  const getActiveFoodTray = (): FoodTray => {
    const foodLine: FoodLine = foodLines.find(f => f.id === containingFoodLineId);
    return foodLine?.foodTrays?.find(
      f => f.columnIndex === selectedFoodTray.columnIndex && f.rowIndex === selectedFoodTray.rowIndex,
    );
  };

  const startTimer = () => {
    setIsActionMenuOpen(false);
    const tray = getActiveFoodTray();
    if (tray.foodItem) {
      if (tray.foodItem.enteredAt) {
        // Food item has an active timer
        // ask what should happen with the old content of the gastronom
        // then refill the gastronom with the same item
        onRestart();
      } else {
        // Food item was changed, but the timer has nog been started yet
        // start timer for the existing food item
        dispatch(foodTrayTemplatesActions.startTimerForExistingFoodItem(tray));
      }
    } else {
      // timer should be started for the food type defined in the template
      dispatch(foodTrayTemplatesActions.startTimer(tray));
    }
  };

  const replaceFoodItem = (item?: FoodType) => {
    setIsActionMenuOpen(false);
    const tray = { ...getActiveFoodTray() };
    if (isRestarting) {
      setIsRestarting(false);
      dispatch(
        foodTrayTemplatesActions.replaceFoodItemAndStartTimer(tray, tray.foodItem.overrideFoodType || tray.defaultFoodType),
      );
    } else if (!hasActiveTimer()) {
      dispatch(foodTrayTemplatesActions.replaceFoodItem(tray, item));
    } else {
      dispatch(foodTrayTemplatesActions.replaceFoodItemAndStartTimer(tray, item, exitReason));
    }
    setExitReason(null);
  };

  const onRestart = () => {
    setIsActionMenuOpen(false);
    setIsFoodStatusOpen(true);
    setIsRestarting(true);
  };

  const emptyFoodTray = (reason: ExitReason) => {
    setIsActionMenuOpen(false);
    const tray = getActiveFoodTray();
    dispatch(foodTrayTemplatesActions.emptyFoodTray(tray, reason));
    setExitReason(null);
  };

  const hasActiveTimer = () => {
    const tray = getActiveFoodTray();
    const timer = tray?.foodItem;
    return timer?.enteredAt && (!timer.exitedAt || isAfter(parseISO(timer.enteredAt), parseISO(timer.exitedAt)));
  };

  const onDelete = () => {
    setIsActionMenuOpen(false);
    setIsDeleting(true);
    setIsFoodStatusOpen(true);
  };

  const onExitReasonSelected = (reason: ExitReason) => {
    setIsActionMenuOpen(false);
    setIsFoodStatusOpen(false);
    if (isRestarting) {
      replaceFoodItem();
    } else {
      if (isDeleting) {
        emptyFoodTray(reason);
      } else {
        setExitReason(reason);
        setIsSelectOpen(true);
      }
      setIsDeleting(false);
    }
  };

  const emptyFoodLine = () => {
    setIsActionMenuOpen(false);
    dispatch(foodTrayTemplatesActions.emptyFoodLine(containingFoodLineId));
    setIsDeleteOpen(false);
  };

  const getName = () => {
    const foodTray = getActiveFoodTray();
    const foodType = foodTray?.foodItem?.overrideFoodType || foodTray?.defaultFoodType;

    return getFoodTypeName(foodType);
  };

  return (
    <div className='food-line-footer'>
      <div className='footer-container'>
        <p className='name'>{getName()}</p>
        <div className='button-container'>
          {!hasActiveTimer() ? (
            <Button
              isDisabled={!getName()}
              onClickCallback={startTimer}
              theme={Theme.SECONDARY}
              icon='Clock'
              isLoading={isStartTimerLoading || isStartTimerForExistingFoodItemLoading}>
              {t('lblStartTimer')}
            </Button>
          ) : (
            <>
              <Button
                onClickCallback={onRestart}
                theme={Theme.SECONDARY}
                icon='Refresh'
                isLoading={isReplaceFoodItemAndStartTimerLoading}>
                {t('lblRefill')}
              </Button>

              <Button onClickCallback={onDelete} theme={Theme.INVERSE} icon='Trashcan' isLoading={isEmptyItemLoading}>
                {t('lblEmpty')}
              </Button>
            </>
          )}
        </div>

        <ActionMenu
          isActionMenuOpen={isActionMenuOpen}
          setIsActionMenuOpen={setIsActionMenuOpen}
          setIsFoodStatusOpen={setIsFoodStatusOpen}
          setIsDeleteOpen={setIsDeleteOpen}
          hasActiveTimer={hasActiveTimer()}
          setIsSelectOpen={setIsSelectOpen}
          isLoading={isEmptyLineLoading || isReplaceFoodItemLoading}
        />
      </div>

      <SelectFoodItemModal
        isOpen={isSelectOpen}
        closeModal={() => setIsSelectOpen(false)}
        onSelectFoodType={replaceFoodItem}
        defaultSelectedFoodType={getActiveFoodTray()?.foodItem?.overrideFoodType || getActiveFoodTray()?.defaultFoodType}
      />
      <FoodStatusModal onSelect={onExitReasonSelected} isOpen={isFoodStatusOpen} close={() => setIsFoodStatusOpen(false)} />
      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        closeModal={() => setIsDeleteOpen(false)}
        description='lblConfirmEmpty'
        title='lblActiveFoodLine'
        onDeleteConfirmed={emptyFoodLine}
        confirmLabel='lblEmpty'
      />
    </div>
  );
};

export default DashboardFooter;
